import React, { useState, useEffect } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useClientMessages } from './Client/ClientMessageContext';
import { useGeneralMessages } from './General/GeneralMessageContext';

interface MessageModalProps {
  className?: string;
}

export const MessageModal: React.FC<MessageModalProps> = ({ className = '' }) => {
  const { visibleMessages: clientMessages, dismissMessage: dismissClientMessage } = useClientMessages();
  const { visibleMessages: generalMessages, dismissMessage: dismissGeneralMessage } = useGeneralMessages();
  const [openModals, setOpenModals] = useState<Set<string>>(new Set());

  // Filter for modal type messages only
  const clientModalMessages = clientMessages.filter(message => 
    message?.data?.displayType === 'MODAL'
  );
  
  const generalModalMessages = generalMessages.filter(message => 
    message?.data?.displayType === 'MODAL'
  );

  // Combine both types of messages
  const allModalMessages = [...clientModalMessages, ...generalModalMessages];

  // Auto-open modals when they become available
  useEffect(() => {
    allModalMessages.forEach(message => {
      if (!openModals.has(message.id)) {
        // Small delay to ensure smooth animation
        const timer = setTimeout(() => {
          setOpenModals(prev => new Set(prev).add(message.id));
        }, 100);
        
        return () => clearTimeout(timer);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModals]);

  if (allModalMessages.length === 0) {
    return null;
  }

  const handleButtonClick = (message: any) => {
    if (!message?.data?.button) return;
    
    const { action, link } = message.data.button;
    
    switch (action) {
      case 'INTERNAL_LINK':
        if (link) {
          window.location.href = link;
        }
        break;
      case 'EXTERNAL_LINK':
        if (link) {
          window.open(link, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'CLOSE':
        // Determine which dismiss function to use based on message source
        if (clientModalMessages.some(m => m.id === message.id)) {
          dismissClientMessage(message.id);
        } else {
          dismissGeneralMessage(message.id);
        }
        break;
      case 'NONE':
      default:
        // Do nothing
        break;
    }
  };

  const handleBackdropClick = (e: React.MouseEvent, messageId: string, isDismissible: boolean) => {
    if (e.target === e.currentTarget && isDismissible) {
      // Determine which dismiss function to use based on message source
      if (clientModalMessages.some(m => m.id === messageId)) {
        dismissClientMessage(messageId);
      } else {
        dismissGeneralMessage(messageId);
      }
    }
  };

  return (
    <RemoveScroll enabled={openModals.size > 0}>
      {allModalMessages.map((message) => {
        const isOpen = openModals.has(message.id);
        const button = message?.data?.button || {};
        const image = message?.data?.image || {};
        const isDismissible = message.data?.dismissible || false;
        
        return (
          <div key={message.id}>
            {/* Modal Overlay */}
            {isOpen && (
              <div 
                className="message-modal-overlay"
                onClick={(e) => handleBackdropClick(e, message.id, isDismissible)}
              >
                <div className="message-modal">
                  <div className="message-modal-header">
                    <h2 className="message-modal-title">{message.data?.title || 'Message'}</h2>
                    {isDismissible && (
                      <button
                        className="message-modal-close"
                        onClick={() => {
                          // Determine which dismiss function to use based on message source
                          if (clientModalMessages.some(m => m.id === message.id)) {
                            dismissClientMessage(message.id);
                          } else {
                            dismissGeneralMessage(message.id);
                          }
                        }}
                        aria-label="Close modal"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                  
                  <div className="message-modal-content">
                    {/* Image Section */}
                    {image.enabled && image.url && (
                      <div className="message-modal-image">
                        <img 
                          src={image.url} 
                          alt={message.data?.title || 'Message image'}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Message Text */}
                    <div className="message-modal-text">
                      <p>{message.data?.message || ''}</p>
                    </div>
                  </div>
                  
                  {/* Modal Actions */}
                  <div className="message-modal-actions">
                    {button.enabled && button.text && (
                      <button
                        className="message-modal-button"
                        onClick={() => handleButtonClick(message)}
                      >
                        {button.text}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </RemoveScroll>
  );
}; 